import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { Component } from 'vue';
import { CSSProperties } from 'vue/types/jsx';

export type LinkedGridCellRenderer<LGC extends Latest.Screen.Designer.LinkedGridCell> = LGC extends Latest.Screen.Designer.RegularGridCell
    ? {
        standardProps: {
            gridCellCfg: LGC;
            /**
             * This prop is used to create a localized utilization of the recursive RenderGrid component.
             * This is helpful when wanted to render nested grid cells using the same styling and logic as the rest of applet runtime, but use generated cells rather than user configured cells
             */
            argoCellCfgs: Latest.Screen.Cells.AppletCell[] | null;
        };
        renderer: () => Promise<Component>;
    }
    : LGC extends Latest.Screen.Designer.SubGridCell
        ? {
            standardProps: {
                gridCellCfg: LGC;
                /**
                 * These props are used to create a localized utilization of the recursive RenderGrid component.
                 * This is helpful when wanted to render nested grid cells using the same styling and logic as the rest of applet runtime, but use generated cells rather than user configured cells
                 */
                argoCellCfgs: Latest.Screen.Cells.AppletCell[] | null;
                allGridCells: Latest.Screen.Designer.LinkedGridCell[] | null;
            };
            renderer: () => Promise<Component>;
        }
        : {
            standardProps: {
                gridCellCfg: LGC;
            };
            renderer: () => Promise<Component>;
        };

type RenderGridWrapperStyle = {
    height: '100%';
    overflow: 'auto' | 'hidden';
    gridArea: string;
    position: 'relative'; // the grid cell content is absolutely positioned within this so that the inset css property can be used to simulate margin
}

export type AllCellsSharedStyle = {
    inset: string;
    borderTop: string;
    fontFamily: string;
    borderLeft: string;
    paddingTop: string;
    borderRight: string;
    paddingLeft: string;
    paddingRight: string;
    position: 'absolute';
    borderBottom: string;
    borderRadius: string;
    paddingBottom: string;
    backgroundColor: string;
}

export interface RegularGridCellStyle extends AllCellsSharedStyle {
    display: 'flex';
    overflow: 'auto' | 'hidden';
    alignItems: 'center';
    justifyContent: 'center';
}

type GenericInfoBoxTextStyle = {
    color: string;
    display: 'flex';
    fontSize: string;
    fontWeight: string;
    alignItems: string;
    justifyContent: string;
};

type InfoBoxGridCellStyle = {
    general: RegularGridCellStyle;
    header: GenericInfoBoxTextStyle & {
        height: '30%';
    };
    content: GenericInfoBoxTextStyle & {
        height: '70%';
    };
};

type SubGridCellStyle = AllCellsSharedStyle & {
    gap: '0px 0px';
    display: 'grid';
    gridTemplateRows: string;
    gridTemplateAreas: string;
    gridTemplateColumns: string;
};

type TabGroupCellStyle = {
    tabGroupStyle: AllCellsSharedStyle;
    tabHeaderStyle: { height: string; } | { width: string };
    // tab content does not get a width option since the way vertical tabs are rendered width doesnt do anything
    tabContentStyle: {
        height: string;
        backgroundColor: 'transparent';
        overflow?: 'hidden';
    };
}

const generatePattern = (payload: {
    rowNum: number;
    colNum: number;
    linkedGridCells: Latest.Screen.Designer.LinkedGridCell[];
}) => {
    let pattern = '';
    for (let rIdx = 0; rIdx < payload.rowNum; rIdx++) {
        pattern += '"';
        for (let cIdx = 0; cIdx < payload.colNum; cIdx++) {
            const lgc = payload.linkedGridCells.find(lgc => lgc.coordinates.find(coord => coord.row === rIdx && coord.column === cIdx));
            if (cIdx === payload.colNum - 1) {
                pattern += `lgc-${lgc?.id || '.'}" `;
            } else {
                pattern += `lgc-${lgc?.id || '.'} `;
            }
        }
    }
    return pattern;
};

export const generateRenderGridCellWrapperStyling = (linkedGridCellId: string): RenderGridWrapperStyle => {
    const style: RenderGridWrapperStyle = {
        height: '100%',
        overflow: 'auto',
        position: 'relative',
        gridArea: `lgc-${linkedGridCellId}`,
    };
    return style;
};

export const generateRegularCellStyle = (linkedGridCellCfg: Latest.Screen.Designer.RegularGridCell | Latest.Screen.Designer.InfoBoxCell, backgroundColor: string, fontFamily: string): RegularGridCellStyle => {
    const style: RegularGridCellStyle = {
        fontFamily,
        backgroundColor,
        display: 'flex',
        overflow: 'auto',
        alignItems: 'center',
        position: 'absolute',
        justifyContent: 'center',
        paddingTop: `${linkedGridCellCfg.padding.top}px`,
        paddingLeft: `${linkedGridCellCfg.padding.left}px`,
        borderRadius: `${linkedGridCellCfg.borderRadius}px`,
        paddingRight: `${linkedGridCellCfg.padding.right}px`,
        paddingBottom: `${linkedGridCellCfg.padding.bottom}px`,
        borderTop: `${linkedGridCellCfg.border.top.width}px ${linkedGridCellCfg.border.top.style} ${linkedGridCellCfg.border.top.color}`,
        borderLeft: `${linkedGridCellCfg.border.left.width}px ${linkedGridCellCfg.border.left.style} ${linkedGridCellCfg.border.left.color}`,
        borderRight: `${linkedGridCellCfg.border.right.width}px ${linkedGridCellCfg.border.right.style} ${linkedGridCellCfg.border.right.color}`,
        borderBottom: `${linkedGridCellCfg.border.bottom.width}px ${linkedGridCellCfg.border.bottom.style} ${linkedGridCellCfg.border.bottom.color}`,
        inset: `
            ${linkedGridCellCfg.margin.top}px
            ${linkedGridCellCfg.margin.right}px
            ${linkedGridCellCfg.margin.bottom}px
            ${linkedGridCellCfg.margin.left}px
        `,
    };
    return style;
};

export const generateInfoBoxCellStyle = (payload: {
    fontFamily: string;
    backgroundColor: string;
    headerFontColor: string;
    contentFontColor: string;
    infoBoxLinkedGridCellCfg: Latest.Screen.Designer.InfoBoxCell;
}): InfoBoxGridCellStyle => {
    const style: InfoBoxGridCellStyle = {
        general: generateRegularCellStyle(payload.infoBoxLinkedGridCellCfg, payload.backgroundColor, payload.fontFamily),
        header: {
            height: '30%',
            display: 'flex',
            color: payload.headerFontColor,
            fontWeight: payload.infoBoxLinkedGridCellCfg.additionalCellProperties.headerFontWeight,
            fontSize: `${payload.infoBoxLinkedGridCellCfg.additionalCellProperties.headerFontSize}px`,
            alignItems: payload.infoBoxLinkedGridCellCfg.additionalCellProperties.headerVerticalAlignment,
            justifyContent: payload.infoBoxLinkedGridCellCfg.additionalCellProperties.headerHorizontalAlignment,
        },
        content: {
            height: '70%',
            display: 'flex',
            color: payload.contentFontColor,
            fontWeight: payload.infoBoxLinkedGridCellCfg.additionalCellProperties.contentFontWeight,
            fontSize: `${payload.infoBoxLinkedGridCellCfg.additionalCellProperties.contentFontSize}px`,
            alignItems: payload.infoBoxLinkedGridCellCfg.additionalCellProperties.contentVerticalAlignment,
            justifyContent: payload.infoBoxLinkedGridCellCfg.additionalCellProperties.contentHorizontalAlignment,
        },
    };
    return style;
};

export const generateSubGridStyle = (payload: {
    fontFamily: string;
    backgroundColor: string;
    filteredLinkedGridCells: Latest.Screen.Designer.LinkedGridCell[];
    subGridCellCfg: Latest.Screen.Designer.SubGridCell | Latest.Screen.Designer.TabInstanceCell | Latest.Screen.Designer.RootGridLayoutCell;
}): SubGridCellStyle => {
    const style: SubGridCellStyle = {
        fontFamily: payload.fontFamily,
        gap: '0px 0px',
        backgroundColor: payload.backgroundColor,
        display: 'grid',
        position: 'absolute',
        paddingTop: `${payload.subGridCellCfg.padding.top}px`,
        paddingLeft: `${payload.subGridCellCfg.padding.left}px`,
        borderRadius: `${payload.subGridCellCfg.borderRadius}px`,
        paddingRight: `${payload.subGridCellCfg.padding.right}px`,
        paddingBottom: `${payload.subGridCellCfg.padding.bottom}px`,
        gridTemplateRows: payload.subGridCellCfg.rows.map(r => `${r.weight}fr`).join(' '),
        gridTemplateColumns: payload.subGridCellCfg.columns.map(c => `${c.weight}fr`).join(' '),
        gridTemplateAreas: generatePattern({
            rowNum: payload.subGridCellCfg.rows.length,
            colNum: payload.subGridCellCfg.columns.length,
            linkedGridCells: payload.filteredLinkedGridCells,
        }),
        borderTop: `${payload.subGridCellCfg.border.top.width}px ${payload.subGridCellCfg.border.top.style} ${payload.subGridCellCfg.border.top.color}`,
        borderLeft: `${payload.subGridCellCfg.border.left.width}px ${payload.subGridCellCfg.border.left.style} ${payload.subGridCellCfg.border.left.color}`,
        borderRight: `${payload.subGridCellCfg.border.right.width}px ${payload.subGridCellCfg.border.right.style} ${payload.subGridCellCfg.border.right.color}`,
        borderBottom: `${payload.subGridCellCfg.border.bottom.width}px ${payload.subGridCellCfg.border.bottom.style} ${payload.subGridCellCfg.border.bottom.color}`,
        inset: `
            ${payload.subGridCellCfg.margin.top}px
            ${payload.subGridCellCfg.margin.right}px
            ${payload.subGridCellCfg.margin.bottom}px
            ${payload.subGridCellCfg.margin.left}px
        `,
    };
    return style;
};

export const generateTabGroupStyle = (tabGroup: Latest.Screen.Designer.TabGroupCell, backgroundColor: string, fontFamily: string): TabGroupCellStyle => {
    let tabHeaderRatio = 20;
    switch (tabGroup.additionalCellProperties.tabHeaderSize) {
        case 'hidden':
            tabHeaderRatio = 0;
            break;
        case 'small':
            tabHeaderRatio = 15;
            break;
        case 'medium':
            tabHeaderRatio = 20;
            break;
        case 'large':
            tabHeaderRatio = 25;
            break;
        case 'x-large':
            tabHeaderRatio = 30;
            break;
        default: {
            const unknownType: string = tabGroup.additionalCellProperties.tabHeaderSize;
            throw new Error(`Unknown tab header size "${unknownType}"`);
        }
    }

    const style: TabGroupCellStyle = {
        tabGroupStyle: {
            fontFamily,
            backgroundColor,
            position: 'absolute',
            paddingTop: `${tabGroup.padding.top}px`,
            paddingLeft: `${tabGroup.padding.left}px`,
            borderRadius: `${tabGroup.borderRadius}px`,
            paddingRight: `${tabGroup.padding.right}px`,
            paddingBottom: `${tabGroup.padding.bottom}px`,
            borderTop: `${tabGroup.border.top.width}px ${tabGroup.border.top.style} ${tabGroup.border.top.color}`,
            borderLeft: `${tabGroup.border.left.width}px ${tabGroup.border.left.style} ${tabGroup.border.left.color}`,
            borderRight: `${tabGroup.border.right.width}px ${tabGroup.border.right.style} ${tabGroup.border.right.color}`,
            borderBottom: `${tabGroup.border.bottom.width}px ${tabGroup.border.bottom.style} ${tabGroup.border.bottom.color}`,
            inset: `
                ${tabGroup.margin.top}px
                ${tabGroup.margin.right}px
                ${tabGroup.margin.bottom}px
                ${tabGroup.margin.left}px
            `,
        },
        tabHeaderStyle: tabGroup.additionalCellProperties.verticalTabs
            ? {
                width: `${tabHeaderRatio}%`,
            }
            : {
                height: `${tabHeaderRatio}%`,
            },
        tabContentStyle: {
            backgroundColor: 'transparent',
            height: `${100 - tabHeaderRatio}%`,
        },
    };
    return style;
};

/** This function generates the styling that is needed around the root of the grid i.e. position relative, aspect-ratio, etc */
export const generateGridWrapperStyling = (): CSSProperties => {
    return {
        backgroundColor: 'rgb(210, 210, 210)',
    };
};
